'use client'

import { useAtomValue, useSetAtom } from 'jotai'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useState } from 'react'
import {
  invalidWalletKeyringAtom,
  isCompleteAtom,
  routeAtom,
  routeMachineSnapshotAtom,
} from '@/src/stores/route-machine/atoms'
import { isCurrentStepModeAtom, viewModeAtom } from '@/src/stores/trade/atoms'
import { cn } from '@/src/utils/styles'
import { useDatadogCustomAttributes } from '../datadog'
import ExpandedBody from '../route-details/expanded-body'
import { RouteMpcDetails } from '../route-details/mpc-screen'
import OverviewFooter from '../route-details/overview-footer'
import OverviewHeader from '../route-details/overview-header'
import { RouteDetailsScreen } from '../route-details/screen'
import WalletError from '../route-details/wallet-error'
import { TradeSteps } from '../trade-steps'
import { ActionsButton } from './actions-buttons'
import { AutoRecoverSuccess } from './auto-recover-success'

interface TradeOverviewProps {
  className?: string
}

export const TradeOverview = ({ className }: TradeOverviewProps) => {
  const route = useAtomValue(routeAtom)
  const isComplete = useAtomValue(isCompleteAtom)
  const missingWalletInfo = useAtomValue(invalidWalletKeyringAtom)
  useDatadogCustomAttributes(route ? { route_id: route.ID } : undefined)
  const snapshot = useAtomValue(routeMachineSnapshotAtom)
  const refundInfo = snapshot.context.refundInfo

  const setMode = useSetAtom(viewModeAtom)
  const isCurrentStepMode = useAtomValue(isCurrentStepModeAtom)
  // Show full-screen route screen
  const [showRoute, setShowRoute] = useState(false)
  // Route-derived autopilot settings screen
  const [showAutopilotSettings, setShowAutopilotSettings] = useState(false)

  // Effect to automatically switch to current-step mode when route is complete
  useEffect(() => {
    if (isComplete && !isCurrentStepMode) {
      setMode('current-step')
    }
  }, [isComplete, isCurrentStepMode, setMode])

  if (!route || !route.ID) return null

  if (missingWalletInfo) {
    return (
      <div className='flex flex-col self-center'>
        <WalletError />
      </div>
    )
  }

  // Auto abort/recovery success
  if (refundInfo && snapshot.value === 'FundsRecoverySuccess') {
    return (
      <div
        className={cn(
          'bg-background border-1 border-border1 rounded-3xl overflow-hidden flex flex-col',
          className,
        )}
      >
        <AutoRecoverSuccess />
      </div>
    )
  }

  if (showAutopilotSettings) {
    return (
      <div
        className={cn(
          'bg-background border-1 border-border1 rounded-3xl overflow-hidden flex flex-col',
          className,
        )}
      >
        <RouteMpcDetails onBack={() => setShowAutopilotSettings(false)} />
      </div>
    )
  }

  if (showRoute) {
    return (
      <div
        className={cn(
          'bg-background border-1 border-border1 rounded-3xl overflow-hidden flex flex-col',
          className,
        )}
      >
        <RouteDetailsScreen onBack={() => setShowRoute(false)} />
      </div>
    )
  }

  return (
    <div
      className={cn(
        'bg-background border-1 border-border1 rounded-3xl overflow-hidden flex flex-col',
        className,
      )}
    >
      <AnimatePresence mode='popLayout'>
        {!isCurrentStepMode && (
          <motion.div
            key='overview-header'
            layoutId='overview-header'
            transition={{ duration: 0.2 }}
            className='w-full'
            exit={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <OverviewHeader
              onClose={() => setMode('current-step')}
              className={className}
            />
          </motion.div>
        )}
      </AnimatePresence>
      <TradeSteps className={className} />
      <AnimatePresence mode='popLayout'>
        {isCurrentStepMode ? (
          <motion.div
            key='current-step'
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className='w-full'
          >
            <ExpandedBody
              onOpenRoute={() => setShowRoute(true)}
              showAutopilotSettings={() => setShowAutopilotSettings(true)}
            />
          </motion.div>
        ) : (
          <motion.div
            key='overview-footer'
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className='w-full'
          >
            <OverviewFooter />
          </motion.div>
        )}
      </AnimatePresence>
      {isCurrentStepMode && <ActionsButton />}
    </div>
  )
}
